import React, { useState, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Procedure } from '../types';
import { BuildingOffice2Icon, RocketLaunchIcon } from '@heroicons/react/24/outline';

interface ProcedureGanttChartProps {
  procedures: Procedure[];
}

type ZoomLevel = 'week' | 'month' | 'quarter' | 'year';

const STATUS_BAR: Record<string, string> = {
  en_attente:     'bg-gray-400',
  en_cours:       'bg-blue-500',
  terminee:       'bg-green-500',
  en_exploitation:'bg-amber-500',
};

function getDisplayStatut(p: Procedure): string {
  if (p.statut === 'EN_EXPLOITATION') return 'en_exploitation';
  if (p.progress === 100) return 'terminee';
  if (p.progress > 0) return 'en_cours';
  return 'en_attente';
}

const ZOOM_LABELS: Record<ZoomLevel, string> = {
  week: 'Semaine', month: 'Mois', quarter: 'Trimestre', year: 'Année',
};

const LABEL_WIDTH = 260;

const ProcedureGanttChart: React.FC<ProcedureGanttChartProps> = ({ procedures }) => {
  const [zoom, setZoom] = useState<ZoomLevel>('month');
  const [hovered, setHovered] = useState<Procedure | null>(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragScrollLeft, setDragScrollLeft] = useState(0);

  const { minDate, maxDate } = useMemo(() => {
    if (!procedures.length) {
      const t = new Date();
      return { minDate: t, maxDate: new Date(t.getTime() + 365 * 86400000) };
    }
    const all = procedures.flatMap(p => [new Date(p.dateDebut), new Date(p.dateFin)]);
    const min = new Date(Math.min(...all.map(d => d.getTime())));
    const max = new Date(Math.max(...all.map(d => d.getTime())));
    const margin = (max.getTime() - min.getTime()) * 0.03;
    min.setTime(min.getTime() - Math.max(margin, 7 * 86400000));
    max.setTime(max.getTime() + Math.max(margin, 7 * 86400000));
    return { minDate: min, maxDate: max };
  }, [procedures]);

  function unitWidth(): number {
    return zoom === 'week' ? 80 : zoom === 'month' ? 50 : zoom === 'quarter' ? 70 : 24;
  }

  function calStart(d: Date, z: ZoomLevel): Date {
    const r = new Date(d); r.setHours(0, 0, 0, 0);
    if (z === 'week') { const dow = r.getDay(); r.setDate(r.getDate() - (dow === 0 ? 6 : dow - 1)); }
    else if (z === 'month') r.setDate(1);
    else if (z === 'quarter') r.setMonth(Math.floor(r.getMonth() / 3) * 3, 1);
    else r.setMonth(0, 1);
    return r;
  }

  function nextColStart(d: Date, z: ZoomLevel): Date {
    const r = new Date(d);
    if (z === 'week') r.setDate(r.getDate() + 7);
    else if (z === 'month') r.setMonth(r.getMonth() + 1);
    else if (z === 'quarter') r.setMonth(r.getMonth() + 3);
    else r.setFullYear(r.getFullYear() + 1);
    return r;
  }

  const columns = useMemo(() => {
    const cols: { date: Date; label: string }[] = [];
    const cur = calStart(new Date(minDate), zoom);
    while (cur <= maxDate) {
      let label = '';
      if (zoom === 'week') {
        const d2 = new Date(Date.UTC(cur.getFullYear(), cur.getMonth(), cur.getDate()));
        const day = d2.getUTCDay() || 7;
        d2.setUTCDate(d2.getUTCDate() + 4 - day);
        const ys = new Date(Date.UTC(d2.getUTCFullYear(), 0, 1));
        const wk = Math.ceil((((d2.getTime() - ys.getTime()) / 86400000) + 1) / 7);
        label = `S${wk} ${cur.toLocaleDateString('fr-FR', { month: 'short' })}`;
      } else if (zoom === 'month') {
        label = cur.toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' });
      } else if (zoom === 'quarter') {
        label = `T${Math.floor(cur.getMonth() / 3) + 1} ${cur.getFullYear()}`;
      } else {
        label = cur.getFullYear().toString();
      }
      cols.push({ date: new Date(cur), label });
      const nxt = nextColStart(cur, zoom);
      cur.setTime(nxt.getTime());
    }
    return cols;
  }, [minDate, maxDate, zoom]);

  function dateToX(date: Date): number {
    const uw = unitWidth();
    for (let i = 0; i < columns.length; i++) {
      const s = columns[i].date;
      const e = i + 1 < columns.length ? columns[i + 1].date : nextColStart(columns[i].date, zoom);
      const dur = e.getTime() - s.getTime();
      if (date.getTime() < e.getTime() || i === columns.length - 1) {
        const frac = dur > 0 ? Math.max(0, (date.getTime() - s.getTime()) / dur) : 0;
        return (i + frac) * uw;
      }
    }
    return columns.length * unitWidth();
  }

  function barPos(p: Procedure) {
    const l = dateToX(new Date(p.dateDebut));
    const r = dateToX(new Date(p.dateFin));
    return { left: l, width: Math.max(r - l, 24) };
  }

  const todayX = dateToX(new Date());
  const totalW = columns.length * unitWidth();

  const fmt = (s: string) => new Date(s).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });

  const onMouseDown = (e: React.MouseEvent) => {
    if (!containerRef.current) return;
    setDragging(true);
    setDragStartX(e.pageX - containerRef.current.offsetLeft);
    setDragScrollLeft(containerRef.current.scrollLeft);
  };
  const onMouseMove = (e: React.MouseEvent) => {
    if (!dragging || !containerRef.current) return;
    e.preventDefault();
    containerRef.current.scrollLeft = dragScrollLeft - (e.pageX - containerRef.current.offsetLeft - dragStartX) * 1.5;
  };
  const onMouseUp = () => setDragging(false);

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden select-none">

      {/* ── Toolbar ── */}
      <div className="flex items-center justify-between px-5 py-3 border-b bg-gray-50">
        <div className="flex items-center gap-2.5">
          <h3 className="text-sm font-semibold text-gray-800">Planification des procédures</h3>
          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-primary-100 text-primary-700">
            {procedures.length} procédure{procedures.length > 1 ? 's' : ''}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 hidden sm:block">Zoom :</span>
          <div className="flex rounded-lg border border-gray-200 overflow-hidden">
            {(Object.keys(ZOOM_LABELS) as ZoomLevel[]).map((z) => (
              <button
                key={z}
                onClick={() => setZoom(z)}
                className={`px-3 py-1.5 text-xs font-medium border-r last:border-r-0 transition-colors ${
                  zoom === z ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {ZOOM_LABELS[z]}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Legend ── */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-5 py-2 border-b bg-gray-50/50 text-xs text-gray-500">
        {[
          { cls: 'bg-gray-400',  label: 'En attente' },
          { cls: 'bg-blue-500',  label: 'En cours' },
          { cls: 'bg-green-500', label: 'Terminée' },
          { cls: 'bg-amber-500', label: 'En exploitation' },
        ].map(({ cls, label }) => (
          <span key={label} className="flex items-center gap-1.5">
            <span className={`inline-block w-3 h-3 rounded ${cls}`} />
            {label}
          </span>
        ))}
        <span className="ml-auto flex items-center gap-1.5">
          <span className="inline-block w-0.5 h-3.5 bg-red-500 rounded" />
          Aujourd'hui
        </span>
      </div>

      {/* ── Body ── */}
      {procedures.length === 0 ? (
        <div className="py-16 text-center text-sm text-gray-400">Aucune procédure à afficher</div>
      ) : (
        <div
          ref={containerRef}
          className={`overflow-auto ${dragging ? 'cursor-grabbing' : 'cursor-grab'}`}
          style={{ maxHeight: 600 }}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
        >
          <div style={{ minWidth: LABEL_WIDTH + totalW }}>

            {/* ── Header row ── */}
            <div className="flex border-b bg-gray-100 sticky top-0 z-10">
              <div
                className="flex-shrink-0 border-r border-gray-200 bg-gray-100 sticky left-0 z-20 flex items-end pb-2 px-3"
                style={{ width: LABEL_WIDTH, minWidth: LABEL_WIDTH }}
              >
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Procédure</span>
              </div>
              <div className="flex flex-shrink-0">
                {columns.map((col, i) => (
                  <div
                    key={i}
                    className="border-r border-gray-200 text-center py-2 text-xs font-medium text-gray-600"
                    style={{ width: unitWidth(), minWidth: unitWidth() }}
                  >
                    {col.label}
                  </div>
                ))}
              </div>
            </div>

            {/* ── Procedure rows ── */}
            {procedures.map((proc, idx) => {
              const statut = getDisplayStatut(proc);
              const barCls = STATUS_BAR[statut] ?? 'bg-gray-400';
              const { left, width } = barPos(proc);

              return (
                <div
                  key={proc.id}
                  className={`flex border-b hover:bg-primary-50/20 transition-colors ${
                    idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'
                  }`}
                  style={{ height: 52 }}
                >
                  {/* Label */}
                  <div
                    className="flex-shrink-0 border-r border-gray-100 bg-white sticky left-0 z-10 flex flex-col justify-center px-3 py-1.5 overflow-hidden"
                    style={{ width: LABEL_WIDTH, minWidth: LABEL_WIDTH }}
                  >
                    <Link
                      to={`/procedures/${proc.id}`}
                      className="text-xs font-semibold text-gray-800 hover:text-primary-700 truncate leading-tight"
                      title={proc.nom}
                    >
                      {proc.nom}
                    </Link>
                    <div className="flex items-center gap-1 mt-0.5">
                      <BuildingOffice2Icon className="h-3 w-3 text-gray-400 flex-shrink-0" />
                      <span className="text-xs text-gray-400 truncate">{proc.structureNom ?? '—'}</span>
                      <span className="ml-auto text-xs font-bold text-primary-600 whitespace-nowrap flex-shrink-0">
                        {proc.ponderationScore}%
                      </span>
                    </div>
                  </div>

                  {/* Timeline */}
                  <div className="flex-1 relative flex-shrink-0" style={{ height: 52, minWidth: totalW }}>
                    {/* Column grid */}
                    <div className="absolute inset-0 flex pointer-events-none">
                      {columns.map((_, i) => (
                        <div
                          key={i}
                          className="border-r border-gray-100 flex-shrink-0"
                          style={{ width: unitWidth(), minWidth: unitWidth(), height: '100%' }}
                        />
                      ))}
                    </div>

                    {/* Today line */}
                    {todayX >= 0 && todayX <= totalW && (
                      <div
                        className="absolute top-0 bottom-0 w-px bg-red-400 z-10 pointer-events-none"
                        style={{ left: todayX }}
                      />
                    )}

                    {/* Bar */}
                    <div
                      className={`absolute top-1/2 -translate-y-1/2 h-7 rounded-md shadow-sm ${barCls}
                        hover:brightness-110 hover:shadow-md transition-all duration-150 overflow-hidden`}
                      style={{ left, width, minWidth: 24 }}
                      onMouseEnter={(e) => {
                        const r = e.currentTarget.getBoundingClientRect();
                        setTooltipPos({ x: r.left + r.width / 2, y: r.top - 8 });
                        setHovered(proc);
                      }}
                      onMouseLeave={() => setHovered(null)}
                    >
                      {/* Ponderation fill overlay (darker = unrealized) */}
                      <div
                        className="absolute inset-0 bg-white/20 rounded-md pointer-events-none"
                        style={{ left: `${proc.ponderationScore}%` }}
                      />
                      {width > 50 && (
                        <span className="absolute inset-0 flex items-center px-2 text-white text-xs font-semibold pointer-events-none">
                          {proc.ponderationScore}%
                        </span>
                      )}
                      {proc.statut === 'EN_EXPLOITATION' && (
                        <RocketLaunchIcon className="absolute right-1.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/80 pointer-events-none" />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

          </div>
        </div>
      )}

      {/* ── Tooltip ── */}
      {hovered && (
        <div
          className="fixed z-50 bg-white rounded-xl shadow-xl border border-gray-200 p-4 w-72 pointer-events-none"
          style={{
            left: Math.min(tooltipPos.x, window.innerWidth - 295),
            top: tooltipPos.y,
            transform: 'translate(-50%, -100%)',
          }}
        >
          <p className="text-sm font-semibold text-gray-900 mb-2 leading-snug">{hovered.nom}</p>
          <div className="space-y-1.5 text-xs">
            <div className="flex items-center gap-1.5 text-gray-500">
              <BuildingOffice2Icon className="h-3.5 w-3.5" />
              <span className="truncate">{hovered.structureNom ?? '—'}</span>
            </div>
            {hovered.structureBeneficiaireNom && (
              <div className="text-indigo-600 text-xs truncate pl-5">{hovered.structureBeneficiaireNom}</div>
            )}
            <div className="flex justify-between text-gray-600 pt-1 border-t border-gray-100">
              <span className="text-gray-400">Début</span>
              <span className="font-medium">{fmt(hovered.dateDebut)}</span>
            </div>
            <div className="flex justify-between text-gray-600">
              <span className="text-gray-400">Échéance</span>
              <span className="font-medium">{fmt(hovered.dateFin)}</span>
            </div>
            <div className="flex justify-between items-center text-gray-600 pt-1 border-t border-gray-100">
              <span className="text-gray-400">Pondération</span>
              <span className="font-bold text-primary-600 text-sm">{hovered.ponderationScore} / 100</span>
            </div>
            <div className="flex justify-between text-gray-600">
              <span className="text-gray-400">Tâches</span>
              <span className="font-medium">{hovered.tachesCompleted} / {hovered.tachesTotal}</span>
            </div>
          </div>
          <div className="absolute left-1/2 bottom-0 -translate-x-1/2 translate-y-full">
            <div className="w-2.5 h-2.5 bg-white border-r border-b border-gray-200 rotate-45 -mt-1.5" />
          </div>
        </div>
      )}
    </div>
  );
};

export default ProcedureGanttChart;
